import EventEmitter from "./EventEmitter";
import { assign } from "./common";

let nextId = 0;

/**
 * Enum for file model state
 * @readonly
 * @enum {string}
 */
export const FileStates = {
  INITIALIZED: "INITIALIZED",
  INVALID: "INVALID",
  PENDING: "PENDING",
  UPLOADING: "UPLOADING",
  OK: "OK",
  FAILED: "FAILED",
  DELETING: "DELETING",
  DELETED: "DELETED"
};

const FileModel = EventEmitter.extend(
  /** @lends FileModel.prototype */
  {
    /**
     * Model class for a file.
     *
     * Can be initialized with a File instance (e.g. from drag'n'drop). Otherwise `file` can be of any type
     * understood by the subclass and the instance is considered "static" - representing a file,
     * which is already uploaded to the server. This is intended for initialization from HTML.
     *
     * @param {File|*} file
     * @param {object} opts
     * @param {number} [opts.maxSize=Infinity]  - maximum size in bytes, has effect on validation
     * @param {number} opts.tooLargeFileMessage - error message for when the file is above maximum size
     * @param {number} opts.adapter             - an {@link UploadAdapter} instance
     * @constructs
     */
    constructor(file, opts) {
      EventEmitter.call(this);
      this.opts = assign(this.getDefaultOptions(), opts);
      this.file = file;
      this.id = nextId++;
      this.error = "";
      if(this.isStatic()) {
        this.sid = this.file.id;
        this.state = FileStates.OK;
      } else {
        this.state = FileStates.INITIALIZED;
        this.validate();
      }
    },

    /**
     * Validate the model and potentially set appropriate state
     * @returns {boolean}
     */
    validate() {
      const valid = !this.opts.maxSize || this.file.size <= this.opts.maxSize;
      if(!valid) {
        this.error = this.opts.tooLargeFileMessage;
        this.setState(FileStates.INVALID);
      }
      return valid;
    },

    /**
     * Whether already uploaded == initialized from HTML
     * @returns {boolean}
     */
    isStatic() {
      return !(this.file instanceof File);
    },

    /**
     * Get file name. Empty string if not set.
     * @returns {string}
     */
    getName() {
      return this.file.name || "";
    },

    /**
     * Get download link.
     * @returns {string}
     */
    getDownloadLink() {
      return this.file.download;
    },

    /**
     * Get file MIME type. `null` is returned if the file is "static".
     * @returns {string}
     */
    getMIME() {
      return this.isStatic() ? null : this.file.type;
    },

    /**
     * Get an  error message if an error occurred (e.g. upload failed)
     */
    getError() {
      return this.error;
    },

    /**
     * Default options factory
     * @returns {object}
     * @protected
     */
    getDefaultOptions() {
      return {
        maxSize: Infinity
      };
    },

    /**
     * Upload the file
     */
    upload() {
      if(this.state !== FileStates.INITIALIZED) return; // already uploaded or failed or invalid

      this.setState(FileStates.UPLOADING);

      this.opts.adapter.uploadFile(
        this,
        (err, responseText) => { this.handleUploadDone(err, responseText); },
        p => { this.handleProgress(p); }
      );

    },

    /**
     * Delete the file on the server or locally, if it's not uploaded
     */
    delete() {
      if(this.state === FileStates.OK) {
        this.setState(FileStates.DELETING);
        this.opts.adapter.deleteFile(this, err => {
          if(err) {
            // TODO: warn
            this.emit("delete_failed");
            console.error(err);
            this.setState(FileStates.OK);
          } else {
            this.setState(FileStates.DELETED);
            this.destroy();
          }
        });
      } else {
        this.setState(FileStates.DELETING);
        this.setState(FileStates.DELETED);
        this.destroy();
      }
    },

    /**
     * Destroy the client-side model (either after deleting on server or deleting locally if invalid)
     * @protected
     */
    destroy() {
      this.stopListening();
      this.emit("destroy");
    },

    /**
     * Callback for the UploadAdapter
     * @param {*} err
     * @param {*} result
     * @protected
     */
    handleUploadDone(err, result) {
      if(err) this.handleUploadError(err);
      else this.handleUploadSuccess(result);
      this.emit("upload_done", result);
    },

    /**
     * Callback for the UploadAdapter upload progress
     * @param progress
     * @protected
     */
    handleProgress(progress) {
      this.emit("progress", { progress });
    },

    /**
     * Handle successful response to upload
     * @param {*} result
     * @protected
     */
    handleUploadSuccess(result) {
      this.sid = result.uid;

      this.file = {
        id: result.uid,
        name: result.filename,
        download: result.url
      };

      this.setState(FileStates.OK);
    },

    /**
     * Handle upload error
     * @param {*} err
     * @protected
     */
    handleUploadError(err) {
      console.error(err);
      // this.error = err.message;
      this.error = this.opts.genericErrorMessage || err.message;
      this.setState(FileStates.FAILED);
    },

    /**
     * Set a state and notify listeners
     * @param {string} state - one of {@link FileStates}
     * @protected
     */
    setState(state) {
      this.emit("state_changed", { state });
      this.state = state;
      this.emit("state_changed_done", { state });
    }
  });

export default FileModel;
